import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import {
  CountryCode,
  CurrencyCode,
  SubscriptionPeriod,
  SubscriptionPlanType,
  useCreateCustomCheckoutLinkMutation,
  useVenueDetailsForCheckoutLazyQuery,
  useVenuesQuery,
} from '../../generated/graphql';

type VenueOption = { id: string; name: string };

type UserOption = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roleName: string;
};

const COUNTRY_CURRENCY_MAP: Partial<Record<CountryCode, CurrencyCode>> = {
  [CountryCode.CzechRepublic]: CurrencyCode.Czk,
  [CountryCode.Slovakia]: CurrencyCode.Eur,
  [CountryCode.Poland]: CurrencyCode.Pln,
  [CountryCode.UnitedKingdom]: CurrencyCode.Gbp,
  [CountryCode.Sweden]: CurrencyCode.Sek,
  [CountryCode.Denmark]: CurrencyCode.Dkk,
  [CountryCode.Norway]: CurrencyCode.Nok,
  [CountryCode.Ireland]: CurrencyCode.Eur,
};

const PLAN_TYPES = [
  SubscriptionPlanType.Tier_1,
  SubscriptionPlanType.Tier_2,
  SubscriptionPlanType.Tier_3,
] as const;

const BILLING_INTERVAL_OPTIONS = [
  { value: SubscriptionPeriod.Monthly, label: 'Monthly' },
  { value: SubscriptionPeriod.Yearly, label: 'Yearly' },
] as const;

export const CustomCheckoutManager = () => {
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [planType, setPlanType] = useState<SubscriptionPlanType>(
    SubscriptionPlanType.Tier_1,
  );
  const [numberOfInvoices, setNumberOfInvoices] = useState(150);
  const [unitAmount, setUnitAmount] = useState<number | ''>('');
  const [billingInterval, setBillingInterval] = useState<SubscriptionPeriod>(
    SubscriptionPeriod.Monthly,
  );
  const [couponId, setCouponId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const { data: venuesData, loading: venuesLoading } = useVenuesQuery();
  const [loadVenue, { data: venueData, loading: venueLoading }] =
    useVenueDetailsForCheckoutLazyQuery();
  const [createCheckoutLink, { loading: creating }] =
    useCreateCustomCheckoutLinkMutation();

  const venues: VenueOption[] = useMemo(() => {
    if (!venuesData?.venues) return [];
    const unique = new Map(
      venuesData.venues.map((venue) => [
        venue.id,
        { id: venue.id, name: venue.name },
      ]),
    );
    return [...unique.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [venuesData]);

  const venueDetails = venueData?.venueDetailsForCheckout;
  const country = venueDetails?.legalEntity?.country as CountryCode | undefined;
  const currencyCode = country ? COUNTRY_CURRENCY_MAP[country] : undefined;

  const venueUsers: UserOption[] = useMemo(() => {
    if (!venueData?.venueUsersForCheckout) return [];
    return venueData.venueUsersForCheckout.map((u) => ({
      id: u.id,
      email: u.account.email,
      firstName: u.account.firstName,
      lastName: u.account.lastName,
      roleName: u.roleName,
    }));
  }, [venueData]);

  const selectedVenue = venues.find((v) => v.id === selectedVenueId) ?? null;

  const handleVenueChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: VenueOption | null,
  ) => {
    const newVenueId = value?.id ?? '';
    setSelectedVenueId(newVenueId);
    setSelectedUser(null);
    setGeneratedUrl('');

    if (value) {
      loadVenue({ variables: { venueId: value.id } });
    }
  };

  const handleUserChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: UserOption | null,
  ) => {
    setSelectedUser(value);
    setGeneratedUrl('');
  };

  const handleGenerate = async () => {
    if (!selectedUser || !country || !currencyCode || !unitAmount) return;

    try {
      const result = await createCheckoutLink({
        variables: {
          input: {
            venueId: selectedVenueId,
            userId: selectedUser.id,
            email: selectedUser.email,
            planType,
            numberOfInvoices,
            unitAmount,
            billingInterval,
            countryCode: country,
            currencyCode,
            couponId: couponId || undefined,
          },
        },
        toastConfig: {
          successText: 'Checkout link generated successfully',
          errorText: 'Failed to generate checkout link',
        },
      });

      if (result?.data?.createCustomCheckoutLink) {
        setGeneratedUrl(result.data.createCustomCheckoutLink);
      }
    } catch (error) {
      console.error('Failed to create checkout link:', error);
    }

    setConfirmOpen(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const canGenerate =
    selectedVenueId &&
    selectedUser &&
    country &&
    currencyCode &&
    unitAmount &&
    !creating;

  return (
    <Box width="100%" p={3} display="flex" justifyContent="center">
      <Box width="100%" maxWidth={800}>
        <Typography variant="h4" gutterBottom>
          Custom Checkout
        </Typography>

        {/* Venue Selection */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Select Venue
          </Typography>
          <Autocomplete<VenueOption>
            fullWidth
            value={selectedVenue}
            options={venues}
            getOptionLabel={(o) => o.name}
            loading={venuesLoading}
            onChange={handleVenueChange}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Venue"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: venuesLoading ? (
                    <CircularProgress size={20} />
                  ) : null,
                }}
              />
            )}
          />
        </Card>

        {/* Venue Details + User Selection */}
        {selectedVenueId && !venueLoading && venueDetails && (
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Venue Details
            </Typography>

            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Venue:</strong> {venueDetails.name}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Country:</strong> {country ?? 'Unknown'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Currency:</strong> {currencyCode ?? 'Unknown'}
            </Typography>

            {venueUsers.length === 0 ? (
              <Alert severity="warning">
                This venue has no users. A user is required to generate a
                checkout link.
              </Alert>
            ) : (
              <Autocomplete<UserOption>
                fullWidth
                value={selectedUser}
                options={venueUsers}
                getOptionLabel={(o) =>
                  `${o.firstName} ${o.lastName} (${o.email}) — ${o.roleName}`
                }
                onChange={handleUserChange}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.firstName} {option.lastName} ({option.email}) —{' '}
                    {option.roleName}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Select User" />
                )}
              />
            )}
          </Card>
        )}

        {venueLoading && (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        )}

        {/* Plan & Pricing */}
        {selectedUser && (
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Plan & Pricing
            </Typography>

            <TextField
              select
              fullWidth
              label="Plan Type"
              value={planType}
              onChange={(e) =>
                setPlanType(e.target.value as SubscriptionPlanType)
              }
              sx={{ mb: 2 }}
            >
              {PLAN_TYPES.map((pt) => (
                <MenuItem key={pt} value={pt}>
                  {pt}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              type="number"
              label="Number of Invoices"
              value={numberOfInvoices}
              onChange={(e) => setNumberOfInvoices(Number(e.target.value))}
              inputProps={{ min: 1 }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="number"
              label={`Price (${currencyCode ?? 'amount'})`}
              value={unitAmount}
              onChange={(e) =>
                setUnitAmount(
                  e.target.value === '' ? '' : Number(e.target.value),
                )
              }
              inputProps={{ min: 0, step: 1 }}
              sx={{ mb: 2 }}
            />

            <TextField
              select
              fullWidth
              label="Billing Interval"
              value={billingInterval}
              onChange={(e) =>
                setBillingInterval(e.target.value as SubscriptionPeriod)
              }
              sx={{ mb: 2 }}
            >
              {BILLING_INTERVAL_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Coupon ID"
              value={couponId}
              onChange={(e) => setCouponId(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ mb: 2, display: 'block' }}
            >
              Optional. For one-off discounts only — standing discounts should
              be set on the venue in Venue Management.
            </Typography>

            <Button
              variant="contained"
              onClick={() => setConfirmOpen(true)}
              disabled={!canGenerate}
              sx={{ mt: 2 }}
            >
              Generate Checkout Link
            </Button>
          </Card>
        )}

        {/* Generated URL */}
        {generatedUrl && (
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Generated Checkout Link
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                wordBreak: 'break-all',
              }}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                {generatedUrl}
              </Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                <IconButton onClick={handleCopy} size="small">
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            </Box>
          </Card>
        )}

        {/* Confirmation Dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Confirm Checkout Link Generation</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Generate a checkout link for{' '}
              <strong>{selectedVenue?.name}</strong>?
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>User:</strong> {selectedUser?.firstName}{' '}
                {selectedUser?.lastName} ({selectedUser?.email})
              </Typography>
              <Typography variant="body2">
                <strong>Plan:</strong> {planType}
              </Typography>
              <Typography variant="body2">
                <strong>Invoices:</strong> {numberOfInvoices}
              </Typography>
              <Typography variant="body2">
                <strong>Amount:</strong> {unitAmount} {currencyCode}
              </Typography>
              <Typography variant="body2">
                <strong>Interval:</strong>{' '}
                {BILLING_INTERVAL_OPTIONS.find(
                  (o) => o.value === billingInterval,
                )?.label ?? billingInterval}
              </Typography>
              <Typography variant="body2">
                <strong>Country:</strong> {country}
              </Typography>
              <Typography variant="body2">
                <strong>Currency:</strong> {currencyCode}
              </Typography>
              {couponId && (
                <Typography variant="body2">
                  <strong>Coupon:</strong> {couponId}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={handleGenerate}
              variant="contained"
              disabled={creating}
            >
              {creating ? <CircularProgress size={20} /> : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};
